import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchUserProfile, patchUserProfile, postChangePassword } from "./profileApi";
import { fetchMarketplaceShop } from "./marketplaceApi";
import { ROLE_PERMISSION_OPTIONS } from "./userMatrixRoleLabels";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { canAccessShop } from "./shopAccess";
import { readStoredAdminFlag } from "../authAdmin";
import { isImpersonating } from "./userMatrixStorage";
import { formatUserFullName } from "./profileNames";
import { DEFAULT_PROFILE_ICON_URL } from "./defaultProfileIcon";
import "./commentAuthorDisplay.css";
import "./marketplaceShop.css";

const PROFILE_CACHE_KEY = "pb.userProfile.v1";
const MAX_ICON_FILE_BYTES = 380 * 1024;

function cosmeticTierTag(item) {
  const tier = item?.tier || "starter";
  if (tier === "legendary") return " ✦";
  if (tier === "rare") return " ◆";
  return "";
}

function writeProfileCache(payload) {
  try {
    localStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({
        email: payload.email || "",
        firstName: payload.firstName || "",
        lastName: payload.lastName || "",
        displayName: payload.displayName || formatUserFullName(payload.firstName, payload.lastName, payload.email),
        profileIconDataUrl: payload.profileIconDataUrl || null,
        updatedAt: Date.now(),
      })
    );
  } catch {
    /* ignore quota */
  }
  window.dispatchEvent(new CustomEvent("pb-user-profile-changed", { detail: payload }));
}

export default function UserProfileSettings() {
  const userMatrixOn = isPluginEnabled(PLUGIN_IDS.USER_MATRIX);
  const marketplaceOn = isPluginEnabled(PLUGIN_IDS.MARKETPLACE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileIconDataUrl, setProfileIconDataUrl] = useState(null);
  const [equippedAvatarCosmeticId, setEquippedAvatarCosmeticId] = useState(null);
  const [equippedUsernameStyleId, setEquippedUsernameStyleId] = useState(null);
  const [equippedBoardThemeId, setEquippedBoardThemeId] = useState(null);
  const [marketplaceOwned, setMarketplaceOwned] = useState([]);
  const [shopCatalog, setShopCatalog] = useState([]);
  const [matrixRoleName, setMatrixRoleName] = useState(null);
  const [matrixPermissions, setMatrixPermissions] = useState({});

  const [profileMessage, setProfileMessage] = useState("");
  const [profileError, setProfileError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [impersonationRev, setImpersonationRev] = useState(0);

  const reload = useCallback(() => {
    setLoadError("");
    setLoading(true);
    Promise.all([
      fetchUserProfile(),
      marketplaceOn ? fetchMarketplaceShop().catch(() => null) : Promise.resolve(null),
    ])
      .then(([p, shop]) => {
        setEmail(p.email || "");
        setFirstName(p.firstName || "");
        setLastName(p.lastName || "");
        setProfileIconDataUrl(p.profileIconDataUrl || null);
        setEquippedAvatarCosmeticId(p.equippedAvatarCosmeticId || null);
        setEquippedUsernameStyleId(p.equippedUsernameStyleId || null);
        setEquippedBoardThemeId(p.equippedBoardThemeId || null);
        setMarketplaceOwned(Array.isArray(p.marketplaceOwned) ? p.marketplaceOwned : shop?.owned || []);
        setShopCatalog(Array.isArray(shop?.catalog) ? shop.catalog : []);
        setMatrixRoleName(p.matrixRoleName || null);
        setMatrixPermissions(p.matrixPermissions && typeof p.matrixPermissions === "object" ? p.matrixPermissions : {});
        writeProfileCache({
          email: p.email,
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          displayName: p.displayName || formatUserFullName(p.firstName, p.lastName, p.email),
          profileIconDataUrl: p.profileIconDataUrl || null,
        });
      })
      .catch((err) => {
        setLoadError(err?.response?.data?.error || err?.message || "Could not load profile.");
      })
      .finally(() => setLoading(false));
  }, [marketplaceOn]);

  useEffect(() => {
    reload();
    const onImpersonation = () => {
      setImpersonationRev((v) => v + 1);
      reload();
    };
    window.addEventListener("pb-impersonation-changed", onImpersonation);
    return () => window.removeEventListener("pb-impersonation-changed", onImpersonation);
  }, [reload]);

  const capabilityLines = useMemo(() => {
    const isAdmin = readStoredAdminFlag() && !isImpersonating();
    const lines = [];
    if (isAdmin) {
      lines.push("Database administrator: full access to administrative APIs and user provisioning.");
    }
    if (!userMatrixOn) {
      lines.push("User Matrix is off: column and role restrictions are not applied to boards.");
      return lines;
    }
    if (matrixRoleName) {
      lines.push(`Assigned role: ${matrixRoleName}.`);
    } else {
      lines.push("No User Matrix role is assigned yet. Ask an administrator to add you to the matrix.");
    }
    const granted = ROLE_PERMISSION_OPTIONS.filter((opt) => matrixPermissions[opt.key] === true);
    if (granted.length === 0 && matrixRoleName) {
      lines.push("This role has no extra application permissions beyond viewing allowed boards.");
    } else {
      for (const g of granted) {
        lines.push(g.label);
      }
    }
    return lines;
  }, [userMatrixOn, matrixRoleName, matrixPermissions, impersonationRev]);

  const ownedSet = useMemo(() => new Set(marketplaceOwned), [marketplaceOwned]);
  const ownedAvatars = useMemo(
    () => shopCatalog.filter((i) => i.type === "avatar" && ownedSet.has(i.id)),
    [shopCatalog, ownedSet]
  );
  const ownedStyles = useMemo(
    () => shopCatalog.filter((i) => i.type === "usernameStyle" && ownedSet.has(i.id)),
    [shopCatalog, ownedSet]
  );
  const ownedThemes = useMemo(
    () => shopCatalog.filter((i) => i.type === "boardTheme" && ownedSet.has(i.id)),
    [shopCatalog, ownedSet]
  );

  const previewStyleClass = useMemo(() => {
    if (!equippedUsernameStyleId) return "";
    const item = shopCatalog.find((i) => i.id === equippedUsernameStyleId);
    return item?.cssClass || "";
  }, [equippedUsernameStyleId, shopCatalog]);

  const activeAvatarPreview = useMemo(() => {
    if (equippedAvatarCosmeticId) {
      const item = shopCatalog.find((i) => i.id === equippedAvatarCosmeticId);
      if (item?.imageDataUrl) return item.imageDataUrl;
    }
    return profileIconDataUrl;
  }, [equippedAvatarCosmeticId, profileIconDataUrl, shopCatalog]);

  const activeAvatarAuraClass = useMemo(() => {
    if (!equippedAvatarCosmeticId) return "";
    const item = shopCatalog.find((i) => i.id === equippedAvatarCosmeticId);
    return item?.auraClass || "";
  }, [equippedAvatarCosmeticId, shopCatalog]);

  const onPickIcon = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) {
      setProfileError("Choose an image file (PNG, JPEG, GIF, or WebP).");
      return;
    }
    if (file.size > MAX_ICON_FILE_BYTES) {
      setProfileError("Image is too large. Use a file under about 380KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === "string" ? reader.result : "";
      if (!/^data:image\/(png|jpeg|jpg|gif|webp);base64,/i.test(url.slice(0, 50))) {
        setProfileError("Unsupported image format.");
        return;
      }
      setProfileIconDataUrl(url);
      setEquippedAvatarCosmeticId(null);
      setProfileError("");
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setProfileMessage("");
    setProfileError("");
    setSavingProfile(true);
    try {
      const body = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };
      if (marketplaceOn) {
        if (equippedAvatarCosmeticId) {
          body.equippedAvatarCosmeticId = equippedAvatarCosmeticId;
          body.profileIconDataUrl = null;
        } else if (profileIconDataUrl) {
          body.profileIconDataUrl = profileIconDataUrl;
        } else {
          body.useInitialsAvatar = true;
        }
        body.equippedUsernameStyleId = equippedUsernameStyleId || null;
        body.equippedBoardThemeId = equippedBoardThemeId || null;
      } else {
        body.profileIconDataUrl = profileIconDataUrl;
      }
      const out = await patchUserProfile(body);
      setFirstName(out.firstName || "");
      setLastName(out.lastName || "");
      setProfileIconDataUrl(out.profileIconDataUrl || null);
      setEquippedAvatarCosmeticId(out.equippedAvatarCosmeticId || null);
      setEquippedUsernameStyleId(out.equippedUsernameStyleId || null);
      setEquippedBoardThemeId(out.equippedBoardThemeId || null);
      if (Array.isArray(out.marketplaceOwned)) setMarketplaceOwned(out.marketplaceOwned);
      writeProfileCache({
        email: out.email || email,
        firstName: out.firstName || "",
        lastName: out.lastName || "",
        displayName: out.displayName || formatUserFullName(out.firstName, out.lastName, out.email || email),
        profileIconDataUrl: out.profileIconDataUrl || null,
      });
      setProfileMessage("Profile saved.");
    } catch (err) {
      setProfileError(err?.response?.data?.error || err?.message || "Could not save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const clearIcon = () => {
    setProfileIconDataUrl(null);
    setEquippedAvatarCosmeticId(null);
    setProfileError("");
    setProfileMessage("");
  };

  const selectShopAvatar = (id) => {
    setEquippedAvatarCosmeticId(id);
    setProfileIconDataUrl(null);
    setProfileError("");
    setProfileMessage("");
  };

  const submitPassword = async (ev) => {
    ev.preventDefault();
    setPasswordMessage("");
    setPasswordError("");
    if (newPassword !== confirmPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await postChangePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage("Password updated.");
    } catch (err) {
      setPasswordError(err?.response?.data?.error || err?.message || "Could not change password.");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <section className="plugins-card user-profile-settings">
        <h2 className="user-profile-settings-title">Your profile</h2>
        <p className="email-triggers-muted">Loading…</p>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className="plugins-card user-profile-settings">
        <h2 className="user-profile-settings-title">Your profile</h2>
        <p className="user-profile-settings-error" role="alert">
          {loadError}
        </p>
        <button type="button" className="user-profile-settings-retry" onClick={reload}>
          Retry
        </button>
      </section>
    );
  }

  const previewName = formatUserFullName(firstName, lastName, email);
  const previewAvatarUrl = activeAvatarPreview || DEFAULT_PROFILE_ICON_URL;
  const isDefaultAvatarPreview = !activeAvatarPreview;

  return (
    <section className="plugins-card user-profile-settings">
      <h2 className="user-profile-settings-title">Your profile</h2>
      <p className="plugins-card-desc user-profile-settings-lead">
        Signed in as <strong>{email}</strong>. Your name and equipped cosmetics are visible on project comments.
      </p>

      <div className="user-profile-settings-grid">
        <div className="user-profile-settings-avatar-block">
          <div
            className={`user-profile-settings-avatar-shell${activeAvatarAuraClass ? ` ${activeAvatarAuraClass}` : ""}`}
          >
            <div
              className={`user-profile-settings-avatar-wrap${
                equippedAvatarCosmeticId ? " user-profile-settings-avatar-wrap--pixel" : ""
              }${isDefaultAvatarPreview ? " user-profile-settings-avatar-wrap--default" : ""}`}
            >
              <img src={previewAvatarUrl} alt="" className="user-profile-settings-avatar-img" />
            </div>
          </div>
          <div className="user-profile-settings-avatar-actions">
            <label className="user-profile-settings-file-label">
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={onPickIcon} />
              <span>Upload photo</span>
            </label>
            {activeAvatarPreview ? (
              <button type="button" className="user-profile-settings-text-btn" onClick={clearIcon}>
                Use default icon
              </button>
            ) : null}
          </div>
        </div>

        <div className="user-profile-settings-fields">
          <label className="user-profile-settings-label">
            First name
            <input
              type="text"
              className="user-profile-settings-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
              placeholder="First name"
              autoComplete="given-name"
            />
          </label>
          <label className="user-profile-settings-label">
            Last name
            <input
              type="text"
              className="user-profile-settings-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
              placeholder="Last name"
              autoComplete="family-name"
            />
          </label>
          {marketplaceOn && previewStyleClass ? (
            <p className="email-triggers-muted">
              Comment preview:{" "}
              <span className={previewStyleClass}>{previewName || email.split("@")[0]}</span>
            </p>
          ) : null}
          {profileError ? (
            <p className="user-profile-settings-error" role="alert">
              {profileError}
            </p>
          ) : null}
          {profileMessage ? (
            <p className="user-profile-settings-success" role="status">
              {profileMessage}
            </p>
          ) : null}
          <button
            type="button"
            className="user-profile-settings-primary"
            onClick={saveProfile}
            disabled={savingProfile}
          >
            {savingProfile ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>

      {marketplaceOn ? (
        <div className="user-profile-cosmetics-section">
          <h3 className="user-profile-settings-subtitle">Marketplace cosmetics</h3>
          <p className="email-triggers-muted">
            {canAccessShop() ? (
              <>
                Buy icons and username styles in the <Link to="/shop">Shop</Link>, then equip them
                here.
              </>
            ) : (
              <>Equip marketplace icons and username styles you already own here.</>
            )}
          </p>

          {ownedAvatars.length > 0 ? (
            <>
              <p className="email-triggers-muted" style={{ marginBottom: 0 }}>
                Profile icons you own
              </p>
              <div className="user-profile-cosmetics-grid">
                {ownedAvatars.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`user-profile-cosmetic-option${
                      equippedAvatarCosmeticId === item.id ? " is-selected" : ""
                    }`}
                    onClick={() => selectShopAvatar(item.id)}
                    title={item.name}
                  >
                    <div
                      className={`user-profile-cosmetic-thumb${item.auraClass ? ` ${item.auraClass}` : ""}`}
                    >
                      <img src={item.imageDataUrl} alt="" />
                    </div>
                    <span className="user-profile-cosmetic-option-label">
                      {item.name}
                      {cosmeticTierTag(item)}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="email-triggers-muted">You have not purchased any profile icons yet.</p>
          )}

          {ownedStyles.length > 0 ? (
            <>
              <p className="email-triggers-muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
                Username styles you own
              </p>
              <div className="user-profile-style-options">
                <label className={`user-profile-style-option${!equippedUsernameStyleId ? " is-selected" : ""}`}>
                  <input
                    type="radio"
                    name="usernameStyle"
                    checked={!equippedUsernameStyleId}
                    onChange={() => setEquippedUsernameStyleId(null)}
                  />
                  <span className="user-profile-style-sample">Default</span>
                </label>
                {ownedStyles.map((item) => (
                  <label
                    key={item.id}
                    className={`user-profile-style-option${
                      equippedUsernameStyleId === item.id ? " is-selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="usernameStyle"
                      checked={equippedUsernameStyleId === item.id}
                      onChange={() => setEquippedUsernameStyleId(item.id)}
                    />
                    <span className={`user-profile-style-sample ${item.cssClass || ""}`}>
                      {previewName || email.split("@")[0] || "Username"}
                    </span>
                    <span className="email-triggers-muted" style={{ marginLeft: "auto", fontSize: 11 }}>
                      {item.name}
                      {cosmeticTierTag(item)}
                    </span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <p className="email-triggers-muted" style={{ marginTop: "0.75rem" }}>
              You have not purchased any username styles yet.
            </p>
          )}

          {ownedThemes.length > 0 ? (
            <>
              <p className="email-triggers-muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
                Board themes you own
              </p>
              <div className="user-profile-theme-options">
                <label className={`user-profile-theme-option${!equippedBoardThemeId ? " is-selected" : ""}`}>
                  <input
                    type="radio"
                    name="boardTheme"
                    checked={!equippedBoardThemeId}
                    onChange={() => setEquippedBoardThemeId(null)}
                  />
                  <span className="user-profile-theme-sample user-profile-theme-sample--default">Default board</span>
                </label>
                {ownedThemes.map((item) => (
                  <label
                    key={item.id}
                    className={`user-profile-theme-option${
                      equippedBoardThemeId === item.id ? " is-selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="boardTheme"
                      checked={equippedBoardThemeId === item.id}
                      onChange={() => setEquippedBoardThemeId(item.id)}
                    />
                    <span className="user-profile-theme-sample">
                      {Array.isArray(item.previewColors)
                        ? item.previewColors.map((color, idx) => (
                            <span
                              key={idx}
                              className="user-profile-theme-swatch"
                              style={{ background: color }}
                              title={item.name}
                            />
                          ))
                        : null}
                      <span className="user-profile-theme-sample-label">{item.name}</span>
                    </span>
                    <span className="email-triggers-muted" style={{ marginLeft: "auto", fontSize: 11 }}>
                      {item.name}
                      {cosmeticTierTag(item)}
                    </span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <p className="email-triggers-muted" style={{ marginTop: "0.75rem" }}>
              You have not purchased any board themes yet.
            </p>
          )}
        </div>
      ) : null}

      <div className="user-profile-settings-capabilities">
        <h3 className="user-profile-settings-subtitle">Your capabilities</h3>
        <p className="email-triggers-muted user-profile-settings-cap-intro">
          Summary of what your account can do in Project Board (from your administrator role and User Matrix role).
        </p>
        <ul className="user-profile-settings-cap-list">
          {capabilityLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <form className="user-profile-settings-password" onSubmit={submitPassword}>
        <h3 className="user-profile-settings-subtitle">Password</h3>
        <p className="email-triggers-muted">Change your login password. You must enter your current password.</p>
        <label className="user-profile-settings-label">
          Current password
          <input
            type="password"
            className="user-profile-settings-input"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        <label className="user-profile-settings-label">
          New password
          <input
            type="password"
            className="user-profile-settings-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
          />
        </label>
        <label className="user-profile-settings-label">
          Confirm new password
          <input
            type="password"
            className="user-profile-settings-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
          />
        </label>
        {passwordError ? (
          <p className="user-profile-settings-error" role="alert">
            {passwordError}
          </p>
        ) : null}
        {passwordMessage ? (
          <p className="user-profile-settings-success" role="status">
            {passwordMessage}
          </p>
        ) : null}
        <button type="submit" className="user-profile-settings-primary" disabled={savingPassword}>
          {savingPassword ? "Updating…" : "Change password"}
        </button>
      </form>
    </section>
  );
}
